import { useState } from "react";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

import { usePromoApply, usePromoRemove, promoErrorMessage } from "./promos";

// Phase 4a promo-codes FR-9 / FR-10. Collapsible "Have a promo code?"
// section that lives inside the cart sidebar (and naturally re-uses
// in the dedicated checkout page once that lands). Hits
// `/api/v1/cart/promo` for apply and remove; on success the cart +
// quote queries are invalidated so the price-breakdown reflects the
// new line.
//
// The "applied" pill is the single source of truth — driven by the
// `promo_code_id` on the cart row (passed in from the parent).
// Removing collapses back to the input row.

interface PromoEntryProps {
  appliedCode: string | null;
}

export function PromoEntry({ appliedCode }: PromoEntryProps): JSX.Element {
  return appliedCode ? <AppliedRow code={appliedCode} /> : <CodeInput />;
}

function CodeInput(): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState("");
  const apply = usePromoApply();

  function submit(): void {
    if (code.trim().length === 0) return;
    apply.mutate(code.trim());
  }

  if (!expanded) {
    return (
      <button
        type="button"
        className="w-full rounded border border-dashed border-zinc-300 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400"
        onClick={() => setExpanded(true)}
        data-testid="promo-toggle"
      >
        Have a promo code?
      </button>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Enter code"
          data-testid="promo-input"
          autoFocus
        />
        <Button
          onClick={submit}
          disabled={apply.isPending || code.trim().length === 0}
          data-testid="promo-apply"
        >
          {apply.isPending ? "Applying…" : "Apply"}
        </Button>
      </div>
      {apply.isError ? (
        <Alert variant="destructive" data-testid="promo-error">
          {promoErrorMessage(apply.error)}
        </Alert>
      ) : null}
    </div>
  );
}

function AppliedRow({ code }: { code: string }): JSX.Element {
  const remove = usePromoRemove();
  return (
    <div
      className="flex items-center justify-between rounded border border-green-600 bg-green-50 px-3 py-2"
      data-testid="promo-applied"
    >
      <span className="text-sm">
        <span className="font-semibold">{code}</span> applied
      </span>
      <button
        type="button"
        className="text-sm text-green-800 underline"
        onClick={() => remove.mutate()}
        disabled={remove.isPending}
        data-testid="promo-remove"
      >
        {remove.isPending ? "Removing…" : "Remove"}
      </button>
    </div>
  );
}
